import { logger } from '@adornis/base/logging.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Category } from '@adornis/buchungsheld-db/entities/category.js';
import { dataviewEmptyState } from '@adornis/buchungsheld-db/global-helpers.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { firstValueFrom, timer } from 'rxjs';
import { BHPromptCategoryModify } from '../../../prompts/bh-prompt-category-modify.js';

/**
 * @element bh-page-admin-einrichtung-categories
 */
@customElement('bh-page-admin-einrichtung-categories')
export class BhPageAdminEinrichtungCategories extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  private readonly _columns = [
    { fieldName: 'name' },
    { fieldName: 'sortingWeight', hidden: true },
    {
      columnName: '',
      customID: '_options',
      selectionSet: { institutionID: 1, sortingWeight: 1 },
      renderItem: doc =>
        html`
          <x-flex space="xs" horizontal crossaxis-start>
            <tr-button-small
              icon="edit"
              .action=${async () => BHPromptCategoryModify.edit(doc)}
              .color=${this.colors.secondary.shade(200)}
            ></tr-button-small>
            <tr-button-small
              .color=${this.colors.tone.error}
              icon="delete"
              .action=${async () => {
                const confirmed = await XDialog.confirm(this._translation.translate('categories', 'confirm_deletion'));

                if (!confirmed) return;

                try {
                  await XDialog.waitFor(Promise.all([doc.remove(), firstValueFrom(timer(500))]));
                } catch (e) {
                  logger.error({ err: e }, 'Es gab ein Problem beim Entfernen der Kategorie.');
                  void XDialog.alert(this._translation.translate('categories', 'err_delete'));
                }
              }}
              .color=${this.colors.secondary.shade(200)}
            ></tr-button-small>
          </x-flex>
        `,
      columnWidth: 'max-content',
    },
  ] as Array<ColumnDefinition<typeof Category>>;

  override render() {
    return html`
      <x-flex space="md">
        <bh-breadcrumb
          .items=${[{ content: this._translation.translate('SIDEBAR', 'categories'), active: true }]}
          .additionalAction=${html`
            <tr-button-small icon="add" .action=${async () => this.addCategory()}>
              <tr-tooltip placement="bottom" .text=${this._translation.translate('create')}> </tr-tooltip>
            </tr-button-small>
          `}
        ></bh-breadcrumb>
        <x-data-view
          .options=${{
            class: Category,
            operation: 'subscribeCatgeoriesPaginated_prop',
            emptyState: () => dataviewEmptyState({}),
            customColumns: this._columns,
            view: new View({ filter: new BHFilter({ institutionID: this.institutionID.value }) }),
          } satisfies DataViewOptions<typeof Category>}
        ></x-data-view>
      </x-flex>
    `;
  }

  async addCategory() {
    const institutionID = this.institutionID.value;
    if (!institutionID) return;

    const category = await BHPromptCategoryModify.add(institutionID);
    if (!category) void XDialog.alert(this._translation.translate('categories', 'err_creation'));
    else void XSnackbar.show(this._translation.translate('actions', 'that_worked'));
  }
}
